import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AIHeaderActionProvider } from './components/AIHeaderAction';
import { useTranslation } from 'react-i18next';
import ReedleToaster from './components/ReedleToaster';
import 'katex/dist/katex.min.css';
import { MS_PER_DAY } from './constants/time';
import './App.css';

// i18n initialization (must be imported before components)
import './i18n';

// Analytics
import { Analytics } from '@vercel/analytics/react';
import {
  initPostHog,
  initGA4,
  initHotjar,
  initClarity,
  // initAmplitude, // [TEMPORARILY DISABLED] Amplitude
  captureCrossDomainParams,
  identifyUser as identifyAnalyticsUser,
  clearUserIdentity as clearAnalyticsIdentity,
  trackEvent,
  setOnceUserProperties,
  updateUserProperties,
} from './lib/analytics';

// Extracted hooks
import useAuth from './hooks/useAuth';
import useGlobalShortcuts from './hooks/useGlobalShortcuts';
import { useFeaturebaseScrollLock } from './hooks/useFeaturebaseScrollLock';
import { useKeyboardScrollAssist } from './hooks/useKeyboardScrollAssist';
import { useVisualViewportKeyboard } from './hooks/useVisualViewportKeyboard';

// Lazy-loaded pages for code splitting
const Login = lazy(() => import('./pages/Login'));
const ArticleList = lazy(() => import('./pages/ArticleList'));
const ArticleReader = lazy(() => import('./pages/ArticleReader'));

// Clock reading for time-based UI (e.g. the PWA dismissal deadline) lives at
// module scope, never inside render.
const APP_BOOT_AT = Date.now();
const FeaturebaseSSO = lazy(() => import('./pages/FeaturebaseSSO'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const SharedArticleView = lazy(() => import('./components/SharedArticleView'));

// Lazy-loaded heavy components
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ExtensionAuthSync = lazy(() => import('./components/ExtensionAuthSync'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Decks = lazy(() => import('./pages/Decks'));
const Me = lazy(() => import('./pages/Me'));
const UsagePage = lazy(() => import('./pages/Usage'));
const MobileSearchPage = lazy(() => import('./components/mobile/MobileSearchPage'));
const DesignSystemLab = import.meta.env.DEV
  ? lazy(() => import('./design-system/lab/DesignSystemLab'))
  : null;

// Agentation visual feedback toolbar (dev only). Renders bottom-right; annotations
// sync to the local `agentation-mcp` HTTP server on 4747, which exposes them over stdio MCP.
const Agentation = import.meta.env.DEV
  ? lazy(() => import('agentation').then((m) => ({ default: m.Agentation })))
  : null;

// Eagerly loaded components (needed for layout and core functionality)
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import ShortcutsModal from './components/ShortcutsModal';
import SearchModal from './components/SearchModal';
import RouteProgressBar from './components/RouteProgressBar';

// Utils
import { cleanupOldStorage } from './utils/cleanupOldStorage';
import { checkForUpdate, UPDATE_CHECK_POPUP_ENABLED } from './services/versionService';
import VersionModal from './components/VersionModal';
import { initFeaturebase, initFeaturebaseChangelog, identifyFeaturebaseUser, clearFeaturebaseIdentity, updateFeaturebaseTheme } from './lib/featurebase';

// PWA install prompt
import PwaInstall from '@khmyznikov/pwa-install/dist/pwa-install.react';

// Deep linking for native apps
import { initDeepLinks } from './lib/deeplink';

// Share import from iOS Share Extension
import { checkAndProcessShares, initShareImportAuthListener, provisionShareToken } from './lib/shareImport';

// Capacitor App plugin for native lifecycle events
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Store
import { ui$, refreshSharedListsCache, setColorTheme } from './store';
import { articles$ } from './store/articles$';

// Legend-State sync utilities for managing sync lifecycle
import { syncState, whenReady, when } from '@legendapp/state';

// Background content fetcher
import { initContentFetcherOnSync, stopContentFetcher } from './lib/contentFetcher';
import NotFound from './pages/NotFound';

// Loading fallback for lazy-loaded components
const PageLoadingFallback = () => {
  const { t } = useTranslation('common');
  return (
    <div className="page-loading-fallback" role="status" aria-live="polite">
      {t('loading')}
    </div>
  );
};

// Reader wrapper component (keeps ArticleReader inside Layout)
const Reader = ({ auth }) => {
  return <ArticleReader auth={auth} />;
};

// Protected route wrapper - redirects to login if not authenticated
// Passes current path as redirect param so user returns here after login
const ProtectedRoute = ({ auth, children }) => {
  const location = useLocation();
  return auth.isAuthenticated
    ? <Layout auth={auth}>{children}</Layout>
    : <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
};

// Global shortcuts wrapper (must be inside Router to use navigation)
const GlobalShortcutsProvider = ({ children }) => {
  useGlobalShortcuts();
  useKeyboardScrollAssist();
  useVisualViewportKeyboard();

  // Track return path for article back button navigation
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // When entering article view from a non-article page, save return path
    if (currentPath.startsWith('/article/') && !previousPath.startsWith('/article/')) {
      ui$.returnPath.set(previousPath);
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  return (
    <>
      {children}
      <ShortcutsModal />
      <SearchModal />
    </>
  );
};

function App() {
  const auth = useAuth();

  // Enable Featurebase widget scroll lock detection
  useFeaturebaseScrollLock();

  // Initialize theme on mount
  useEffect(() => {
    setColorTheme(ui$.colorTheme.get());
  }, []);

  // Clean up old localStorage keys after migration to IndexedDB
  // Runs once after app loads successfully (after auth is determined)
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      cleanupOldStorage();
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Refresh shared list cache for "hide shared articles" filter optimization
  // This enables instant filtering on /all page without async RPC calls per list
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      refreshSharedListsCache();
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Boot-time version check (P4-b, ADR-004): per-channel row, always logged.
  // Popup gated by UPDATE_CHECK_POPUP_ENABLED in versionService (on since
  // 2026-10-25; only rows with a `min` can trigger it); below-min native clients
  // get the blocking dialog (store/APK buttons) instead of a useless reload.
  const [forceUpdateInfo, setForceUpdateInfo] = useState(null);
  useEffect(() => {
    if (auth.isLoading) return;
    checkForUpdate('reedle').then((result) => {
      console.log('[App] update-check', {
        channel: result.channel,
        key: result.key,
        local: result.localVersion,
        remote: result.remoteVersion,
        belowMin: result.belowMin,
        error: result.error,
      });
      if (UPDATE_CHECK_POPUP_ENABLED && result.belowMin && !result.error) {
        setForceUpdateInfo({
          latest: result.remoteVersion,
          min: result.minVersion,
          force_update: true,
          release_notes: result.releaseNotes,
        });
      }
    });
  }, [auth.isLoading]);

  // Start background content fetcher when articles$ sync completes
  // Uses Legend State's syncState to observe isLoaded state
  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      // Initialize fetcher - it will start when articles$ isLoaded becomes true
      const cleanup = initContentFetcherOnSync();
      return cleanup;
    } else {
      // Stop fetcher when logged out
      stopContentFetcher();
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Initialize Featurebase feedback widget and changelog popup with current theme
  useEffect(() => {
    // The mount effect above has already migrated colorTheme to light|dark.
    const featurebaseTheme = ui$.colorTheme.get();
    initFeaturebase({ theme: featurebaseTheme });
    initFeaturebaseChangelog({ theme: featurebaseTheme });

    // Restore "hide feedback" state if still within the 30-min window
    try {
      const hiddenUntil = parseInt(localStorage.getItem('reedle-feedback-hidden-until'), 10);
      if (hiddenUntil && Date.now() < hiddenUntil) {
        document.body.classList.add('feedback-temporarily-hidden');
        const remaining = hiddenUntil - Date.now();
        setTimeout(() => document.body.classList.remove('feedback-temporarily-hidden'), remaining);
      } else {
        localStorage.removeItem('reedle-feedback-hidden-until');
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  // Initialize deep linking for native apps (handles reedle:// and universal links)
  useEffect(() => {
    initDeepLinks();
  }, []);

  // Initialize share import (iOS Share Extension support)
  // Checks for pending shares on cold start and sets up auth listener
  useEffect(() => {
    initShareImportAuthListener();

    // Check for pending shares after BOTH auth AND sync are ready
    const initShareImport = async () => {
      if (auth.isLoading || !auth.isAuthenticated) return;

      // Wait for articles$ sync to be fully ready before processing shares
      // This prevents race condition where articles are created before sync handlers are initialized
      const syncStatus$ = syncState(articles$);

      // Wait for IndexedDB persistence to load
      await whenReady(() => syncStatus$.isPersistLoaded);

      // Wait for sync handlers to be registered and active
      await when(() => syncStatus$.isSyncEnabled);

      // NOW safe to process shares - articles will sync to Supabase
      checkAndProcessShares();

      // Ensure share extension PAT is provisioned on every cold start
      // (onChange listener in initShareImportAuthListener only fires on future changes,
      // not the current value — so returning users with cached sessions need this)
      provisionShareToken();
    };

    initShareImport();

    // Warm-start: when app resumes from background, check for new shares
    // (e.g. user shared via Share Extension while app was backgrounded,
    // or Android MainActivity.onNewIntent() stored a pending file share).
    // Note: also fires on cold start, but harmless — native checkPendingShares
    // clears UserDefaults atomically on first read, so the second call gets empty.
    if (Capacitor.isNativePlatform()) {
      const listenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) checkAndProcessShares();
      });
      return () => { listenerPromise.then(l => l.remove()); };
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  // Initialize analytics services
  useEffect(() => {
    initPostHog();
    initGA4();
    initHotjar();
    initClarity();
    // initAmplitude(); // [TEMPORARILY DISABLED] Amplitude

    // Capture cross-domain tracking from reedle-site
    captureCrossDomainParams();
  }, []);

  // Sync Featurebase widget theme with app theme changes
  useEffect(() => {
    // Subscribe to colorTheme changes
    // Legend-State passes ListenerParams, not the raw value.
    const unsubscribe = ui$.colorTheme.onChange(({ value }) => {
      updateFeaturebaseTheme(value);
    });

    return unsubscribe;
  }, []);

  // Identify user and track session events - fires only on user identity change (login), not token refresh
  useEffect(() => {
    if (!auth.user) return;

    // Identify in Featurebase
    identifyFeaturebaseUser(auth.user.id, {
      email: auth.user.email,
      name: auth.user.username || auth.user.email?.split('@')[0],
      profilePicture: auth.user.avatar_url,
    });

    // Identify in analytics services (PostHog, GA4, Hotjar, Clarity)
    identifyAnalyticsUser(auth.user.id, {
      email: auth.user.email,
      username: auth.user.username || auth.user.email?.split('@')[0],
    });

    const now = new Date();

    // Track first_login - localStorage guard ensures it fires only once per device/browser
    const FIRST_LOGIN_KEY = 'reedle_first_login_tracked';
    if (!localStorage.getItem(FIRST_LOGIN_KEY)) {
      const signupDate = auth.user.created_at || now.toISOString();
      setOnceUserProperties({
        first_login_at: now.toISOString(),
        signup_date: signupDate,
      });
      trackEvent('first_login', { user_id: auth.user.id });
      localStorage.setItem(FIRST_LOGIN_KEY, '1');
    }

    // Session tracking - 30-min cooldown prevents noise from token refreshes / re-renders
    const LAST_SESSION_KEY = 'reedle_last_session_at';
    const SESSION_NUMBER_KEY = 'reedle_session_number';
    const SESSION_TRACK_KEY = 'reedle_last_session_started_tracked_at';
    const SESSION_COOLDOWN_MS = 30 * 60 * 1000;
    const lastTrackedStr = localStorage.getItem(SESSION_TRACK_KEY);
    const shouldTrackSession = !lastTrackedStr || (now - new Date(lastTrackedStr)) > SESSION_COOLDOWN_MS;

    if (shouldTrackSession) {
      const lastSessionStr = localStorage.getItem(LAST_SESSION_KEY);
      let daysSinceLastSession = 0;
      let sessionNumber = 1;

      if (lastSessionStr) {
        const lastSession = new Date(lastSessionStr);
        daysSinceLastSession = Math.floor((now - lastSession) / MS_PER_DAY);
        sessionNumber = parseInt(localStorage.getItem(SESSION_NUMBER_KEY) || '1', 10) + 1;
      }

      localStorage.setItem(LAST_SESSION_KEY, now.toISOString());
      localStorage.setItem(SESSION_NUMBER_KEY, String(sessionNumber));
      localStorage.setItem(SESSION_TRACK_KEY, now.toISOString());

      trackEvent('session_started', {
        days_since_last_session: daysSinceLastSession,
        session_number: sessionNumber,
      });
      updateUserProperties({
        last_session_at: now.toISOString(),
        session_number: sessionNumber,
      });
    }

    // Update days_since_signup and track D1/D7 milestones
    if (auth.user.created_at) {
      const signupDateObj = new Date(auth.user.created_at);
      const daysSinceSignup = Math.floor((now - signupDateObj) / MS_PER_DAY);
      updateUserProperties({ days_since_signup: daysSinceSignup });

      if (daysSinceSignup >= 1 && daysSinceSignup < 2) {
        setOnceUserProperties({ returned_d1_at: now.toISOString() });
        trackEvent('user_returned_d1', {});
      }
      if (daysSinceSignup >= 7 && daysSinceSignup < 8) {
        setOnceUserProperties({ returned_d7_at: now.toISOString() });
        trackEvent('user_returned_d7', {});
      }
    }
  }, [auth.user?.id]);

  // Clear identity and track signed_out when user logs out
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      clearFeaturebaseIdentity();
      clearAnalyticsIdentity();
      trackEvent('signed_out', {});
    }
  }, [auth.isAuthenticated, auth.isLoading]);

  // Check if this is an extension auth request
  const isExtensionAuth = window.location.search.includes('from=extension');

  // Show loading state while auth is being determined
  // This prevents redirecting to /login then /inbox on refresh
  if (auth.isLoading) {
    return (
      <div className="loading" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        color: '#999'
      }}>
        Loading...
      </div>
    );
  }

  const isPwaDismissed = () => {
    try {
      const until = parseInt(localStorage.getItem('reedle-pwa-dismissed'), 10);
      // Boot timestamp (module scope) — reading the clock during render keeps
      // the banner stable for the whole session instead of flickering at the
      // dismissal boundary.
      return until && APP_BOOT_AT < until;
    } catch { return false; }
  };

  const showPwaInstall = !auth.isLoading && auth.isAuthenticated && !Capacitor.isNativePlatform() && !isPwaDismissed();

  const handlePwaUserChoice = () => {
    try {
      localStorage.setItem('reedle-pwa-dismissed', String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    } catch { /* localStorage unavailable — the prompt simply reappears */ }
  };

  return (
    <HelmetProvider>
    <AIHeaderActionProvider>
    <Router>
      <RouteProgressBar />
      {forceUpdateInfo && (
        <VersionModal isOpen forcedInfo={forceUpdateInfo} onClose={() => {}} />
      )}
      <GlobalShortcutsProvider>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Public shared article route - no auth required */}
            <Route path="/shared/article/:token" element={<SharedArticleView />} />

            <Route path="/login" element={
            // If from extension and already logged in, show sync page
            isExtensionAuth && auth.isAuthenticated
              ? <ExtensionAuthSync auth={auth} />
              : (!auth.isAuthenticated ? <Login auth={auth} /> : <Navigate to="/dashboard" />)
          } />

            {/* Default route redirects to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute auth={auth}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Route-level settings page */}
            <Route
              path="/settings/:tab?"
              element={
                <ProtectedRoute auth={auth}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* All articles view */}
            <Route
              path="/all"
              element={
                <ProtectedRoute auth={auth}>
                  <ArticleList auth={auth} view="all" />
                </ProtectedRoute>
              }
            />

            {/* Inbox view */}
            <Route
              path="/inbox"
              element={
                <ProtectedRoute auth={auth}>
                  <ArticleList auth={auth} view="inbox" />
                </ProtectedRoute>
              }
            />

            {/* Specific list view */}
            <Route
              path="/list/:listId"
              element={
                <ProtectedRoute auth={auth}>
                  <ArticleList auth={auth} />
                </ProtectedRoute>
              }
            />

            {/* Decks index — the Decks tab's destination */}
            <Route
              path="/decks"
              element={
                <ProtectedRoute auth={auth}>
                  <Decks />
                </ProtectedRoute>
              }
            />

            {/* Me — identity, streak, and everything the sidebar carried below the lists */}
            <Route path="/me/usage" element={<ProtectedRoute auth={auth}><UsagePage /></ProtectedRoute>} />
            <Route
              path="/me"
              element={
                <ProtectedRoute auth={auth}>
                  <Me />
                </ProtectedRoute>
              }
            />

            {/* Search — the phone shell's pushed search page (desktop search is the Cmd+K modal) */}
            <Route
              path="/search"
              element={
                <ProtectedRoute auth={auth}>
                  <MobileSearchPage />
                </ProtectedRoute>
              }
            />

            {/* Deck view */}
            <Route
              path="/deck/:deckId"
              element={
                <ProtectedRoute auth={auth}>
                  <ArticleList auth={auth} />
                </ProtectedRoute>
              }
            />

            {/* Article reader */}
            <Route
              path="/article/:id"
              element={
                <ProtectedRoute auth={auth}>
                  <Reader auth={auth} />
                </ProtectedRoute>
              }
            />

            {/* Payment success page */}
            <Route path="/payment-success" element={<PaymentSuccess />} />

            {/* Password reset page - handles redirect from password reset email */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Featurebase SSO - handles redirect from Featurebase for authentication */}
            <Route path="/sso/featurebase" element={<FeaturebaseSSO auth={auth} />} />

            {/* Development-only component and theme catalog */}
            {DesignSystemLab && <Route path="/dev/design-system" element={<DesignSystemLab />} />}

            {/* 404 catch-all — must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </GlobalShortcutsProvider>
    </Router>
    <ReedleToaster />
    <Analytics />
    {Agentation && (
      <Suspense fallback={null}>
        <Agentation endpoint="http://localhost:4747" appName="Reedle" />
      </Suspense>
    )}
    {showPwaInstall && (
      <PwaInstall
        manifestUrl="/manifest.webmanifest"
        icon="/icons/icon-192x192.png"
        onPwaUserChoiceResultEvent={handlePwaUserChoice}
        onPwaInstallSuccessEvent={handlePwaUserChoice}
      />
    )}
    </AIHeaderActionProvider>
    </HelmetProvider>
  );
}

function AppRoot() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppRoot;
